/**
 * Combat audio engine — a faithful TypeScript port of the approved combat audio
 * director reference. Node graph, numeric constants and cue structure are the
 * reference's; only the types are new.
 *
 * The graph is: every voice lands on the music bus or the SFX bus, the SFX bus
 * is driven then compressed then made up again, both buses meet at master,
 * master is compressed once more and passes an analyser on its way out. Two
 * sends hang off the voices themselves — a cross-fed stereo delay and a
 * convolution reverb built from a synthesised impulse — so a cue can place
 * itself in the room without moving the bus balance.
 *
 * The approved player shot is a pre-rendered original one-shot; the remaining
 * cues retain the reference's oscillators, pink-ish noise buffer and 25% pulse
 * wave. `VoiceAllocator` keeps those procedural cues from turning to mush.
 *
 * DOM, canvas scope and control UI from the reference are deliberately absent;
 * the harness owns presentation. `CombatDirector` owns the combo state.
 */

export type CombatPhase = 'normal' | 'rush' | 'fever' | 'overdrive';
export type ImpactProfileId = 'classic' | 'punch' | 'max';
export type NoteBus = 'music' | 'sfx';
export type NoteType = 'sine' | 'square' | 'triangle' | 'sawtooth' | 'pulse25' | 'noise';
export type ShockRingVariant = 'fever' | 'overdrive';

export interface PhaseInfo {
  readonly phase: CombatPhase;
  readonly intensity: number;
}

export interface VoiceStats {
  readonly active: number;
  readonly limit: number;
  readonly stolen: number;
  readonly dropped: number;
}

/** A scheduled, stoppable sound. The allocator only ever needs these fields. */
export interface Voice {
  readonly id: string;
  readonly tag: string;
  readonly note: string;
  readonly priority: number;
  /** `performance.now()` at registration — the tie-breaker for equal priorities. */
  readonly startedAt: number;
  readonly startAt: number;
  readonly endAt: number;
  readonly startPerf: number;
  readonly endPerf: number;
  stop(stolen?: boolean): void;
}

export interface NoteFilter {
  readonly type?: BiquadFilterType;
  readonly start?: number;
  readonly end?: number;
  readonly q?: number;
}

export interface NoteOptions {
  readonly midi?: number;
  readonly duration?: number;
  readonly when?: number;
  readonly type?: NoteType;
  readonly gain?: number;
  readonly attack?: number;
  readonly release?: number;
  readonly pan?: number;
  readonly priority?: number;
  readonly bus?: NoteBus;
  readonly detune?: number;
  readonly filter?: NoteFilter;
  readonly tag?: string;
  readonly glideTo?: number;
  readonly echo?: number;
  readonly reverb?: number;
}

interface LayerOptions {
  readonly when: number;
  readonly gain?: number;
  readonly pan?: number;
  readonly priority?: number;
  readonly tag?: string;
}

interface TransientOptions extends LayerOptions {
  readonly bright?: number;
  readonly duration?: number;
}

interface SubImpactOptions extends LayerOptions {
  readonly midi?: number;
  readonly glideTo?: number;
  readonly duration?: number;
}

interface BellOptions extends LayerOptions {
  readonly midi: number;
  readonly duration?: number;
  readonly detune?: number;
  readonly reverb?: number;
}

interface ImpactProfile {
  readonly gain: number;
  readonly drive: number;
  readonly reverb: number;
  readonly delay: number;
  readonly density: number;
  readonly label: string;
}

/** Every node built at unlock. Held as one object so a single null check narrows the graph. */
interface EngineNodes {
  readonly ctx: AudioContext;
  /** false when ctx was handed in from outside (Fix 1 — shared with AudioEngine):
   *  dispose() must never close a context it doesn't own. */
  readonly ownsContext: boolean;
  readonly master: GainNode;
  readonly musicBus: GainNode;
  readonly sfxBus: GainNode;
  /** 完成済みテクノSEを既存のSFXドライブから分離して、その音色を変えずに通す。 */
  readonly technoBus: GainNode;
  readonly analyser: AnalyserNode;
  readonly sfxDrive: WaveShaperNode;
  readonly sfxMakeup: GainNode;
  readonly delayInput: GainNode;
  readonly delayWet: GainNode;
  readonly reverbInput: GainNode;
  readonly reverbWet: GainNode;
  readonly noiseBuffer: AudioBuffer;
  readonly pulseWave: PeriodicWave;
}

/** Fix 1: the convolver reverb send and the cross-feedback delay send are the
 *  "always-on heavy nodes" — connected to master only while a voice is actually
 *  feeding them, disconnected (fully unreachable from destination, so the engine
 *  can skip processing them) after this many ms of no new activity. Reverb's tail
 *  covers the ~1.32s impulse response; delay's covers a few feedback repeats. */
const REVERB_IDLE_DISCONNECT_MS = 1500;
const DELAY_IDLE_DISCONNECT_MS = 900;

type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
const midiToHz = (midi: number): number => 440 * Math.pow(2, (midi - 69) / 12);
const midiName = (midi: number): string =>
  (NOTE_NAMES[((Math.round(midi) % 12) + 12) % 12] ?? 'C') + String(Math.floor(midi / 12) - 1);
const ratioToCents = (ratio: number): number => 1200 * Math.log2(ratio);

export const PHASE_CENTS: Record<CombatPhase, number> = {
  normal: 0,
  rush: ratioToCents(1.12),
  fever: ratioToCents(1.20),
  overdrive: ratioToCents(1.10),
};

const IMPACT_PROFILES: Record<ImpactProfileId, ImpactProfile> = {
  classic: { gain: .86, drive: 1.15, reverb: .115, delay: .10, density: 0, label: 'CLASSIC · compact transient + restrained tail' },
  punch: { gain: .98, drive: 1.65, reverb: .165, delay: .14, density: 1, label: 'PUNCH · stronger body + wider cadence + short bloom' },
  max: { gain: 1.06, drive: 2.15, reverb: .225, delay: .175, density: 2, label: 'MAX · transient snap + sub body + stereo bloom + phase cadence' },
};

/** Master level of the reference; the harness's mute rides on top of it. */
const MASTER_LEVEL = .72;
/** R2の完成済みSEをBGMの上物として収める固定ヘッドルーム（約-3.35dB）。 */
const TECHNO_BUS_TRIM = .68;
const TECHNO_EVENT_SAMPLE_URLS = Object.freeze([
  '/assets/audio/r2/techno/event-00-pickup.wav',
  '/assets/audio/r2/player-shot-a-01.wav',
  '/assets/audio/r2/techno/event-02-small-kill.wav',
  '/assets/audio/r2/techno/event-03-power-down.wav',
  '/assets/audio/r2/techno/event-04-large-kill.wav',
  '/assets/audio/r2/techno/event-05-laser.wav',
  '/assets/audio/r2/techno/event-06-ricochet.wav',
  '/assets/audio/r2/techno/event-07-tether.wav',
  '/assets/audio/r2/techno/event-08-device-repair.wav',
  '/assets/audio/r2/techno/event-09-device-phase.wav',
  '/assets/audio/r2/techno/event-10-device-time.wav',
  '/assets/audio/r2/techno/event-11-device-overdrive.wav',
  '/assets/audio/r2/techno/event-12-boss-spawn.wav',
  '/assets/audio/r2/techno/event-13-boss-phase.wav',
  '/assets/audio/r2/techno/event-14-boss-defeat.wav',
  '/assets/audio/r2/techno/event-15-player-down.wav',
  '/assets/audio/r2/techno/event-16-gate-open.wav',
  '/assets/audio/r2/techno/event-17-purchase-ok.wav',
  '/assets/audio/r2/techno/event-18-purchase-fail.wav',
  '/assets/audio/r2/techno/event-19-weapon-level.wav',
]);
const TECHNO_EVENT_SAMPLE_GAINS = new Float32Array([
  .34, .48, .42, .38, .52, .4, .32, .4, .42, .44,
  .42, .54, .5, .54, .58, .5, .4, .3, .28, .4,
]);
const TECHNO_COMBO_SAMPLE_URLS = Object.freeze([
  '/assets/audio/r2/techno/combo-rush.wav',
  '/assets/audio/r2/techno/combo-storm.wav',
  '/assets/audio/r2/techno/combo-overdrive.wav',
  '/assets/audio/r2/techno/combo-break.wav',
]);
const TECHNO_FORMATION_SAMPLE_URLS = Object.freeze([
  '/assets/audio/r2/techno/formation-front.wav',
  '/assets/audio/r2/techno/formation-rear.wav',
  '/assets/audio/r2/techno/formation-orbit.wav',
  '/assets/audio/r2/techno/formation-wide.wav',
  '/assets/audio/r2/techno/formation-spear.wav',
  '/assets/audio/r2/techno/formation-shelter.wav',
]);
const TECHNO_COMBO_SAMPLE_GAIN = .38;
const TECHNO_FORMATION_SAMPLE_GAIN = .3;
const PLAYER_SHOT_PAN = .08;
type OscillatorNoteType = 'sine' | 'square' | 'triangle' | 'sawtooth';
const OSCILLATOR_TYPES: readonly NoteType[] = ['sine', 'square', 'triangle', 'sawtooth'];
const isOscillatorType = (type: NoteType): type is OscillatorNoteType => OSCILLATOR_TYPES.includes(type);

/** Not every engine has one; the reference degrades to a mono send and so does this. */
const makePanner = (c: AudioContext): StereoPannerNode | null =>
  typeof c.createStereoPanner === 'function' ? c.createStereoPanner() : null;

export class VoiceAllocator {
  limit: number;
  readonly active = new Set<Voice>();
  stolen = 0;
  dropped = 0;
  private readonly listeners = new Set<(allocator: VoiceAllocator) => void>();

  constructor(limit = 32) {
    this.limit = limit;
  }

  get liveCount(): number {
    const now = performance.now();
    let count = 0;
    this.active.forEach((v) => {
      if (v.startPerf <= now && now < v.endPerf) count += 1;
    });
    return count;
  }

  stats(): VoiceStats {
    return { active: this.liveCount, limit: this.limit, stolen: this.stolen, dropped: this.dropped };
  }

  overlaps(voice: Voice): Voice[] {
    return [...this.active].filter((v) => v.startAt < voice.endAt && voice.startAt < v.endAt);
  }

  selectVictim(candidates: readonly Voice[] = [...this.active]): Voice | null {
    return [...candidates].sort((a, b) => a.priority - b.priority || a.startedAt - b.startedAt)[0] ?? null;
  }

  setLimit(limit: number): void {
    this.limit = limit;
    const now = performance.now();
    let sounding = [...this.active].filter((v) => v.startPerf <= now && now < v.endPerf);
    while (sounding.length > limit) {
      const v = this.selectVictim(sounding);
      if (!v) break;
      this.stolen += 1;
      v.stop(true);
      sounding = sounding.filter((x) => x !== v);
    }
    this.emit();
  }

  register(voice: Voice): boolean {
    const overlapping = this.overlaps(voice);
    if (overlapping.length >= this.limit) {
      const victim = this.selectVictim(overlapping);
      if (!victim || victim.priority > voice.priority) {
        this.dropped += 1;
        this.emit();
        return false;
      }
      this.stolen += 1;
      victim.stop(true);
    }
    this.active.add(voice);
    this.emit();
    return true;
  }

  release(voice: Voice): void {
    this.active.delete(voice);
    this.emit();
  }

  panic(): void {
    [...this.active].forEach((v) => v.stop(true));
    this.active.clear();
    this.emit();
  }

  onChange(fn: (allocator: VoiceAllocator) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  emit(): void {
    this.listeners.forEach((fn) => fn(this));
  }
}

export class CombatAudioEngine {
  readonly allocator = new VoiceAllocator(32);
  phase: CombatPhase = 'normal';
  impactProfile: ImpactProfileId = 'max';
  private nodes: EngineNodes | null = null;
  private musicLevel = .46;
  private sfxLevel = .78;
  private mutedFlag = false;
  /** Fix 1: reverb/delay sends are connected to master only while in use. */
  private reverbConnected = false;
  private delayConnected = false;
  private reverbDisconnectTimer = 0;
  private delayDisconnectTimer = 0;
  private technoEventBuffers: readonly AudioBuffer[] = [];
  private technoComboBuffers: readonly AudioBuffer[] = [];
  private technoFormationBuffers: readonly AudioBuffer[] = [];
  private technoSampleLoad: Promise<void> | null = null;

  get ctx(): AudioContext | null {
    return this.nodes?.ctx ?? null;
  }

  get state(): string {
    return this.nodes?.ctx.state ?? 'suspended';
  }

  get muted(): boolean {
    return this.mutedFlag;
  }

  get sfxVolume(): number {
    return this.sfxLevel;
  }

  get musicVolume(): number {
    return this.musicLevel;
  }

  profile(): ImpactProfile {
    return IMPACT_PROFILES[this.impactProfile];
  }

  rich(level = 1): boolean {
    return this.profile().density >= level && (level < 2 || this.allocator.limit >= 24);
  }

  /**
   * Fix 1: pass the host's already-unlocked AudioContext (e.g. AudioEngine's) so
   * this engine builds its node graph on that single shared context instead of
   * opening a second live AudioContext. Callers with no host context (the asset
   * harness) still get one created here, exactly as before.
   */
  async unlock(sharedContext?: AudioContext): Promise<AudioContext> {
    let nodes = this.nodes;
    if (!nodes) {
      let c = sharedContext;
      if (!c) {
        const legacy = (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
        const AudioCtx: AudioContextCtor | undefined = window.AudioContext ?? legacy;
        if (!AudioCtx) throw new Error('Web Audio API is not supported.');
        c = new AudioCtx({ latencyHint: 'interactive' });
      }
      const ownsContext = sharedContext === undefined;
      const master = c.createGain();
      const musicBus = c.createGain();
      const sfxBus = c.createGain();
      const technoBus = c.createGain();
      const analyser = c.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = .52;

      const sfxDrive = c.createWaveShaper();
      sfxDrive.oversample = '2x';
      const sfxComp = c.createDynamicsCompressor();
      sfxComp.threshold.value = -19;
      sfxComp.knee.value = 8;
      sfxComp.ratio.value = 3.8;
      sfxComp.attack.value = .0012;
      sfxComp.release.value = .09;
      const sfxMakeup = c.createGain();
      sfxMakeup.gain.value = .88;

      const masterComp = c.createDynamicsCompressor();
      masterComp.threshold.value = -11;
      masterComp.knee.value = 10;
      masterComp.ratio.value = 4.2;
      masterComp.attack.value = .0025;
      masterComp.release.value = .145;

      const delayInput = c.createGain();
      const delayWet = c.createGain();
      const delayL = c.createDelay(.6);
      const delayR = c.createDelay(.6);
      const filterL = c.createBiquadFilter();
      const filterR = c.createBiquadFilter();
      const panL = makePanner(c);
      const panR = makePanner(c);
      const feedbackL = c.createGain();
      const feedbackR = c.createGain();
      delayL.delayTime.value = .108;
      delayR.delayTime.value = .171;
      filterL.type = filterR.type = 'lowpass';
      filterL.frequency.value = 3900;
      filterR.frequency.value = 3500;
      feedbackL.gain.value = .12;
      feedbackR.gain.value = .105;
      delayInput.connect(delayL);
      delayInput.connect(delayR);
      delayL.connect(filterL);
      delayR.connect(filterR);
      filterL.connect(feedbackL);
      feedbackL.connect(delayR);
      filterR.connect(feedbackR);
      feedbackR.connect(delayL);
      if (panL && panR) {
        panL.pan.value = -.62;
        panR.pan.value = .62;
        filterL.connect(panL);
        filterR.connect(panR);
        panL.connect(delayWet);
        panR.connect(delayWet);
      } else {
        filterL.connect(delayWet);
        filterR.connect(delayWet);
      }
      // delayWet → master is connected on demand (ensureDelayConnected), not here —
      // see Fix 1: this send stays fully unreachable from destination while idle.

      const reverbInput = c.createGain();
      const reverbWet = c.createGain();
      const highpass = c.createBiquadFilter();
      const convolver = c.createConvolver();
      const lowpass = c.createBiquadFilter();
      highpass.type = 'highpass';
      highpass.frequency.value = 220;
      lowpass.type = 'lowpass';
      lowpass.frequency.value = 5600;
      convolver.buffer = this.makeImpulse(c, 1.32, 4.8);
      reverbInput.connect(highpass);
      highpass.connect(convolver);
      convolver.connect(lowpass);
      lowpass.connect(reverbWet);
      // reverbWet → master likewise connects on demand (ensureReverbConnected).

      musicBus.gain.value = this.musicLevel;
      sfxBus.gain.value = this.sfxLevel;
      technoBus.gain.value = this.sfxLevel * TECHNO_BUS_TRIM;
      master.gain.value = this.mutedFlag ? 0 : MASTER_LEVEL;
      musicBus.connect(master);
      sfxBus.connect(sfxDrive);
      sfxDrive.connect(sfxComp);
      sfxComp.connect(sfxMakeup);
      sfxMakeup.connect(master);
      technoBus.connect(master);
      master.connect(masterComp);
      masterComp.connect(analyser);
      analyser.connect(c.destination);

      nodes = {
        ctx: c,
        ownsContext,
        master,
        musicBus,
        sfxBus,
        technoBus,
        analyser,
        sfxDrive,
        sfxMakeup,
        delayInput,
        delayWet,
        reverbInput,
        reverbWet,
        noiseBuffer: this.makeNoiseBuffer(c, 1.6),
        pulseWave: this.makePulseWave(c, .25),
      };
      this.nodes = nodes;
      this.applyImpactProfile();
    }
    if (nodes.ctx.state !== 'running') await nodes.ctx.resume();
    await this.prepareTechnoSampleBank(nodes.ctx);
    return nodes.ctx;
  }

  /**
   * 全テクノSEはゲーム開始前に完全デコードする。失敗時に旧合成音へ戻すと
   * 編成が崩れるため、フォールバックせずunlockを失敗させる。
   */
  private async prepareTechnoSampleBank(context: AudioContext): Promise<void> {
    if (
      this.technoEventBuffers.length === TECHNO_EVENT_SAMPLE_URLS.length
      && this.technoComboBuffers.length === TECHNO_COMBO_SAMPLE_URLS.length
      && this.technoFormationBuffers.length === TECHNO_FORMATION_SAMPLE_URLS.length
    ) return;
    if (!this.technoSampleLoad) {
      const decode = async (urls: readonly string[]): Promise<readonly AudioBuffer[]> => {
        const encoded = await Promise.all(urls.map(async (url) => {
          const response = await fetch(url, { cache: 'force-cache' });
          if (!response.ok) throw new Error(`Techno SE sample load failed: ${response.status} ${url}`);
          return response.arrayBuffer();
        }));
        const decoded: AudioBuffer[] = [];
        // Fetch in parallel, but decode one at a time. Concurrent decodeAudioData
        // bursts can starve iPhone's live Web Audio renderer during unlock.
        for (let i = 0; i < encoded.length; i += 1) {
          decoded.push(await context.decodeAudioData(encoded[i]!));
        }
        return decoded;
      };
      this.technoSampleLoad = (async () => {
        const events = await decode(TECHNO_EVENT_SAMPLE_URLS);
        const combos = await decode(TECHNO_COMBO_SAMPLE_URLS);
        const formations = await decode(TECHNO_FORMATION_SAMPLE_URLS);
        this.technoEventBuffers = events;
        this.technoComboBuffers = combos;
        this.technoFormationBuffers = formations;
      })();
    }
    try {
      await this.technoSampleLoad;
    } catch (error) {
      this.technoSampleLoad = null;
      throw error;
    }
  }

  private makeNoiseBuffer(c: AudioContext, seconds: number): AudioBuffer {
    const n = Math.floor(c.sampleRate * seconds);
    const buffer = c.createBuffer(1, n, c.sampleRate);
    const data = buffer.getChannelData(0);
    let seed = 0x51f15e;
    let filtered = 0;
    for (let i = 0; i < n; i += 1) {
      seed ^= seed << 13;
      seed ^= seed >>> 17;
      seed ^= seed << 5;
      const white = ((seed >>> 0) / 4294967295) * 2 - 1;
      filtered = filtered * .58 + white * .42;
      data[i] = filtered;
    }
    return buffer;
  }

  private makeImpulse(c: AudioContext, seconds: number, decay: number): AudioBuffer {
    const length = Math.floor(c.sampleRate * seconds);
    const buffer = c.createBuffer(2, length, c.sampleRate);
    for (let ch = 0; ch < 2; ch += 1) {
      const data = buffer.getChannelData(ch);
      let seed = 0x6d2b79f5 ^ (ch * 0x9e3779b9);
      let smooth = 0;
      for (let i = 0; i < length; i += 1) {
        seed ^= seed << 13;
        seed ^= seed >>> 17;
        seed ^= seed << 5;
        const white = ((seed >>> 0) / 4294967295) * 2 - 1;
        smooth = smooth * .18 + white * .82;
        const env = Math.pow(1 - i / length, decay);
        data[i] = smooth * env * (ch ? -.92 : 1);
      }
    }
    return buffer;
  }

  private makePulseWave(c: AudioContext, duty: number): PeriodicWave {
    const n = 48;
    const real = new Float32Array(n);
    const imag = new Float32Array(n);
    for (let i = 1; i < n; i += 1) imag[i] = (2 / (i * Math.PI)) * Math.sin(i * Math.PI * duty);
    return c.createPeriodicWave(real, imag, { disableNormalization: false });
  }

  private makeDriveCurve(amount: number): Float32Array<ArrayBuffer> {
    const n = 2048;
    const curve = new Float32Array(n);
    const norm = Math.tanh(amount);
    for (let i = 0; i < n; i += 1) {
      const x = i * 2 / (n - 1) - 1;
      curve[i] = Math.tanh(x * amount) / norm;
    }
    return curve;
  }

  applyImpactProfile(): void {
    const p = this.profile();
    const nodes = this.nodes;
    if (!nodes) return;
    nodes.sfxDrive.curve = this.makeDriveCurve(p.drive);
    nodes.sfxMakeup.gain.setTargetAtTime(
      this.impactProfile === 'max' ? .92 : this.impactProfile === 'punch' ? .88 : .83,
      nodes.ctx.currentTime,
      .025,
    );
    nodes.reverbWet.gain.setTargetAtTime(p.reverb, nodes.ctx.currentTime, .035);
    nodes.delayWet.gain.setTargetAtTime(p.delay, nodes.ctx.currentTime, .035);
  }

  setImpactProfile(mode: ImpactProfileId): void {
    this.impactProfile = mode;
    this.applyImpactProfile();
  }

  setPhase(phase: CombatPhase): void {
    this.phase = phase;
  }

  setMusicVolume(v: number): void {
    this.musicLevel = v;
    const nodes = this.nodes;
    if (nodes) nodes.musicBus.gain.setTargetAtTime(v, nodes.ctx.currentTime, .025);
  }

  setSfxVolume(v: number): void {
    this.sfxLevel = v;
    const nodes = this.nodes;
    if (nodes) {
      nodes.sfxBus.gain.setTargetAtTime(v, nodes.ctx.currentTime, .025);
      nodes.technoBus.gain.setTargetAtTime(v * TECHNO_BUS_TRIM, nodes.ctx.currentTime, .025);
    }
  }

  /** Harness affordance, not in the reference: master to silence and back. */
  setMuted(muted: boolean): void {
    this.mutedFlag = muted;
    const nodes = this.nodes;
    if (nodes) nodes.master.gain.setTargetAtTime(muted ? 0 : MASTER_LEVEL, nodes.ctx.currentTime, .012);
  }

  toggleMute(): void {
    this.setMuted(!this.mutedFlag);
  }

  duckMusic(depth = .4, duration = .08, attack = .006, hold = .012, delay = 0): void {
    const nodes = this.nodes;
    if (!nodes) return;
    const now = nodes.ctx.currentTime + Math.max(0, delay);
    const g = nodes.musicBus.gain;
    const end = Math.max(now + .045, now + duration);
    g.cancelScheduledValues(now);
    g.setValueAtTime(Math.max(.0001, g.value), now);
    g.linearRampToValueAtTime(Math.max(.018, this.musicLevel * depth), now + attack);
    g.setValueAtTime(Math.max(.018, this.musicLevel * depth), now + attack + hold);
    g.exponentialRampToValueAtTime(Math.max(.0001, this.musicLevel), end);
  }

  /**
   * Fix 1: the reverb send (reverbWet → master) is the gate for the whole
   * convolver chain — with it disconnected, highpass/convolver/lowpass/reverbWet
   * have no path to destination, so the engine has nothing to pull from them.
   * Connecting is idempotent and always cancels a pending disconnect first.
   */
  private ensureReverbConnected(nodes: EngineNodes): void {
    window.clearTimeout(this.reverbDisconnectTimer);
    if (this.reverbConnected) return;
    this.reverbConnected = true;
    nodes.reverbWet.connect(nodes.master);
  }

  private scheduleReverbDisconnect(nodes: EngineNodes): void {
    window.clearTimeout(this.reverbDisconnectTimer);
    this.reverbDisconnectTimer = window.setTimeout(() => {
      this.reverbConnected = false;
      try {
        nodes.reverbWet.disconnect(nodes.master);
      } catch { /* already disconnected */ }
    }, REVERB_IDLE_DISCONNECT_MS);
  }

  /** Same idea as the reverb pair above, for the cross-fed stereo delay send. */
  private ensureDelayConnected(nodes: EngineNodes): void {
    window.clearTimeout(this.delayDisconnectTimer);
    if (this.delayConnected) return;
    this.delayConnected = true;
    nodes.delayWet.connect(nodes.master);
  }

  private scheduleDelayDisconnect(nodes: EngineNodes): void {
    window.clearTimeout(this.delayDisconnectTimer);
    this.delayDisconnectTimer = window.setTimeout(() => {
      this.delayConnected = false;
      try {
        nodes.delayWet.disconnect(nodes.master);
      } catch { /* already disconnected */ }
    }, DELAY_IDLE_DISCONNECT_MS);
  }

  note(options: NoteOptions): Voice | null {
    const nodes = this.nodes;
    if (!nodes) return null;
    const {
      midi = 60,
      duration = .2,
      when,
      type = 'square',
      gain = .12,
      attack = .003,
      release = .08,
      pan = 0,
      priority = 10,
      bus = 'music',
      detune = 0,
      filter,
      tag = 'note',
      glideTo,
      echo = 0,
      reverb = 0,
    } = options;
    const c = nodes.ctx;
    const startAt = Math.max(c.currentTime, when ?? c.currentTime);
    const endAt = startAt + Math.max(.015, duration);
    const output = bus === 'sfx' ? nodes.sfxBus : nodes.musicBus;
    const actualGain = Math.max(.0002, gain * (bus === 'sfx' ? this.profile().gain : 1));
    const amp = c.createGain();
    const panner = makePanner(c);
    const echoGain = echo > 0 ? c.createGain() : null;
    const reverbGain = reverb > 0 ? c.createGain() : null;
    let source: AudioScheduledSourceNode;
    let filterNode: BiquadFilterNode | null = null;
    let cleanupTimer = 0;
    let ended = false;
    if (type === 'noise') {
      const noise = c.createBufferSource();
      noise.buffer = nodes.noiseBuffer;
      noise.playbackRate.setValueAtTime(Math.pow(2, detune / 1200), startAt);
      source = noise;
    } else {
      const osc = c.createOscillator();
      if (type === 'pulse25') osc.setPeriodicWave(nodes.pulseWave);
      else osc.type = isOscillatorType(type) ? type : 'sine';
      osc.frequency.setValueAtTime(midiToHz(midi), startAt);
      osc.detune.setValueAtTime(detune, startAt);
      if (glideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(20, midiToHz(glideTo)), endAt);
      source = osc;
    }
    amp.gain.setValueAtTime(.0001, startAt);
    amp.gain.exponentialRampToValueAtTime(actualGain, startAt + Math.max(.001, attack));
    amp.gain.setValueAtTime(actualGain, Math.max(startAt + attack, endAt - release));
    amp.gain.exponentialRampToValueAtTime(.0001, endAt);
    if (filter) {
      filterNode = c.createBiquadFilter();
      filterNode.type = filter.type ?? 'lowpass';
      filterNode.frequency.setValueAtTime(Math.max(30, filter.start ?? 6000), startAt);
      filterNode.Q.value = filter.q ?? .7;
      if (filter.end) filterNode.frequency.exponentialRampToValueAtTime(Math.max(30, filter.end), endAt);
    }
    const nowPerf = performance.now();
    const voice: Voice = {
      id: Math.random().toString(36).slice(2, 9),
      tag,
      note: type === 'noise' ? 'NOISE' : midiName(midi),
      priority,
      startedAt: nowPerf,
      startAt,
      endAt,
      startPerf: nowPerf + Math.max(0, (startAt - c.currentTime) * 1000),
      endPerf: nowPerf + Math.max(0, (endAt - c.currentTime) * 1000),
      stop: (stolen = false): void => {
        if (ended) return;
        ended = true;
        window.clearTimeout(cleanupTimer);
        const t = c.currentTime;
        try {
          amp.gain.cancelScheduledValues(t);
          amp.gain.setTargetAtTime(.0001, t, stolen ? .0035 : .01);
          source.stop(t + (stolen ? .018 : .04));
        } catch { /* the source may already be finished */ }
        this.allocator.release(voice);
      },
    };
    if (!this.allocator.register(voice)) {
      try {
        source.disconnect();
        amp.disconnect();
      } catch { /* nothing was connected yet */ }
      return null;
    }
    source.connect(filterNode ?? amp);
    if (filterNode) filterNode.connect(amp);
    const sendNode: AudioNode = panner ?? amp;
    if (panner) {
      panner.pan.value = clamp(pan, -1, 1);
      amp.connect(panner);
      panner.connect(output);
    } else amp.connect(output);
    if (echoGain) {
      echoGain.gain.value = echo;
      sendNode.connect(echoGain);
      echoGain.connect(nodes.delayInput);
      this.ensureDelayConnected(nodes);
    }
    if (reverbGain) {
      reverbGain.gain.value = reverb;
      sendNode.connect(reverbGain);
      reverbGain.connect(nodes.reverbInput);
      this.ensureReverbConnected(nodes);
    }
    source.onended = (): void => {
      if (!ended) {
        ended = true;
        this.allocator.release(voice);
      }
      try {
        source.disconnect();
        amp.disconnect();
        filterNode?.disconnect();
        panner?.disconnect();
        echoGain?.disconnect();
        reverbGain?.disconnect();
      } catch { /* already torn down */ }
      // Fix 1: only voices that actually fed the send need to push its idle timer out.
      if (echoGain) this.scheduleDelayDisconnect(nodes);
      if (reverbGain) this.scheduleReverbDisconnect(nodes);
    };
    try {
      source.start(startAt);
      source.stop(endAt + .035);
    } catch {
      voice.stop();
    }
    cleanupTimer = window.setTimeout(() => voice.stop(), Math.max(40, (endAt - c.currentTime + .2) * 1000));
    return voice;
  }

  detune(extra = 0): number {
    return extra + PHASE_CENTS[this.phase];
  }

  transient({ when, gain = .12, pan = 0, priority = 90, tag = 'impact:snap', bright = 9200, duration = .024 }: TransientOptions): void {
    this.note({
      type: 'noise', duration, when, gain, attack: .0008, release: duration * .76, priority, bus: 'sfx', tag, pan,
      filter: { type: 'highpass', start: bright, end: Math.max(1800, bright * .36), q: .55 }, reverb: .05,
    });
  }

  subImpact({ when, midi = 44, glideTo = 27, duration = .2, gain = .2, priority = 91, pan = 0, tag = 'impact:sub' }: SubImpactOptions): void {
    this.note({ midi, glideTo, duration, when, type: 'sine', gain, attack: .001, release: duration * .82, priority, bus: 'sfx', tag, pan });
  }

  bell({ midi, when, duration = .3, gain = .13, priority = 70, pan = 0, tag = 'bell', detune = 0, reverb = .3 }: BellOptions): void {
    this.note({ midi, when, duration, type: 'sine', gain, attack: .0015, release: duration * .76, priority, pan, bus: 'sfx', tag, detune, echo: .21, reverb });
    this.note({ midi: midi + 19, when, duration: duration * .64, type: 'sine', gain: gain * .27, attack: .001, release: duration * .58, priority: priority - 1, pan, bus: 'sfx', tag: `${tag}:partial`, detune, reverb: reverb * .75 });
    if (this.rich(2) && this.allocator.limit >= 24) {
      this.note({ midi: midi + 31, when: when + .008, duration: duration * .42, type: 'sine', gain: gain * .105, attack: .001, release: duration * .38, priority: priority - 3, pan: -pan * .7, bus: 'sfx', tag: `${tag}:air-partial`, detune, reverb: reverb * .85 });
    }
  }

  playTechnoEvent(
    id: number,
    when?: number,
    pan = 0,
    levelScale = 1,
    maxDuration?: number,
  ): AudioBufferSourceNode | null {
    const buffer = this.technoEventBuffers[id];
    const gain = TECHNO_EVENT_SAMPLE_GAINS[id] ?? .4;
    return this.playTechnoSample(buffer, gain * levelScale, when, pan, maxDuration);
  }

  playTechnoCombo(index: number, when?: number): void {
    this.playTechnoSample(this.technoComboBuffers[index], TECHNO_COMBO_SAMPLE_GAIN, when, 0);
  }

  playTechnoFormation(
    index: number,
    when?: number,
    levelScale = 1,
    maxDuration?: number,
  ): AudioBufferSourceNode | null {
    return this.playTechnoSample(
      this.technoFormationBuffers[index],
      TECHNO_FORMATION_SAMPLE_GAIN * levelScale,
      when,
      0,
      maxDuration,
    );
  }

  shot(when?: number, levelScale = 1, pan = PLAYER_SHOT_PAN): AudioBufferSourceNode | null {
    return this.playTechnoEvent(1, when, pan, levelScale);
  }

  private playTechnoSample(
    buffer: AudioBuffer | undefined,
    level: number,
    when?: number,
    panValue = 0,
    maxDuration?: number,
  ): AudioBufferSourceNode | null {
    const nodes = this.nodes;
    if (!nodes || !buffer || nodes.ctx.state !== 'running') return null;

    const source = nodes.ctx.createBufferSource();
    const gain = nodes.ctx.createGain();
    const panner = makePanner(nodes.ctx);
    source.buffer = buffer;
    gain.gain.value = level;
    if (panner) panner.pan.value = panValue;
    source.connect(gain);
    if (panner) {
      gain.connect(panner);
      panner.connect(nodes.technoBus);
    } else {
      gain.connect(nodes.technoBus);
    }
    source.onended = (): void => {
      source.disconnect();
      gain.disconnect();
      panner?.disconnect();
    };
    const startAt = Math.max(nodes.ctx.currentTime, when ?? nodes.ctx.currentTime);
    source.start(startAt);
    if (maxDuration !== undefined) source.stop(startAt + Math.max(.006, maxDuration));
    return source;
  }

  enemyHit(): void {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + .003;
    const d = this.detune();
    this.transient({ when: t, gain: .115, priority: 75, tag: 'enemy:hit-snap', bright: 8300, duration: .021 });
    this.note({ type: 'noise', duration: .062, when: t + .003, gain: .135, attack: .001, release: .047, priority: 72, bus: 'sfx', tag: 'enemy:hit', detune: d, filter: { type: 'bandpass', start: 4200, end: 920, q: 1.65 } });
    this.note({ midi: 55, glideTo: 48, duration: .052, when: t, type: 'triangle', gain: .13, attack: .001, release: .039, priority: 73, bus: 'sfx', tag: 'enemy:hit-body', detune: d });
  }

  normalKill(combo = 1): void {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + .003;
    const d = this.detune();
    const phase = this.phase;
    this.duckMusic(phase === 'normal' ? .72 : phase === 'rush' ? .61 : phase === 'fever' ? .48 : .39, .063, .003, .009);
    this.transient({ when: t, gain: phase === 'overdrive' ? .17 : .145, priority: 86, tag: 'kill:snap', bright: 9900, duration: .026 });
    this.subImpact({ when: t + .002, midi: phase === 'overdrive' ? 41 : 49, glideTo: phase === 'overdrive' ? 23 : 31, duration: phase === 'overdrive' ? .19 : .15, gain: phase === 'overdrive' ? .235 : .195, priority: 85, tag: 'kill:body' });
    this.note({ midi: 76, glideTo: 55, duration: .085, when: t + .004, type: 'sawtooth', gain: .075, attack: .001, release: .066, priority: 82, bus: 'sfx', tag: 'kill:crack', detune: d, filter: { type: 'bandpass', start: 5100, end: 760, q: 2.7 }, pan: -.08 });
    const sparks = this.rich(2) ? 5 : this.rich(1) ? 4 : 3;
    for (let i = 0; i < sparks; i += 1) {
      this.note({ midi: 88 + i * 4 + (i % 2 ? 3 : 0), duration: .052 + i * .006, when: t + .018 + i * .012, type: i % 2 ? 'square' : 'sine', gain: .068 / (1 + i * .12), attack: .001, release: .041 + i * .004, priority: 79 - i, bus: 'sfx', tag: 'kill:spark', detune: d, pan: -.72 + i * (1.44 / Math.max(1, sparks - 1)), echo: .11, reverb: .14 });
    }
    if (combo > 0 && combo % 4 === 0) {
      this.bell({ midi: 92 + (combo % 8 ? 0 : 5), when: t + .055, duration: .19, gain: .066, priority: 81, pan: .34, tag: 'kill:four-beat', detune: d, reverb: .24 });
    }
  }

  mediumExplosion(combo = 1): void {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + .003;
    const d = this.detune();
    this.duckMusic(this.phase === 'fever' ? .25 : .34, .13, .003, .022);
    // F1–F2: contact and two-frame white flash translated into a dry transient pair.
    this.transient({ when: t, gain: .185, priority: 96, tag: 'medium:contact', bright: 11400, duration: .021, pan: -.08 });
    this.transient({ when: t + .007, gain: .125, priority: 95, tag: 'medium:white-flash', bright: 9600, duration: .027, pan: .09 });
    // F3: orange core expansion: pitched body + filtered blast.
    this.subImpact({ when: t + .011, midi: 46, glideTo: 25, duration: .31, gain: .285, priority: 97, tag: 'medium:core-body' });
    this.note({ type: 'noise', duration: .34, when: t + .014, gain: .22, attack: .001, release: .29, priority: 95, bus: 'sfx', tag: 'medium:blast', detune: d, filter: { start: 7600, end: 115, q: 1.25 }, reverb: .24 });
    // F4: eight-direction debris, deliberately staggered so it reads as matter rather than hiss.
    const shardMidi = [91, 84, 96, 79, 88, 101, 82, 94];
    shardMidi.forEach((m, i) => this.note({ midi: m, glideTo: m - 10 - i % 3, duration: .09 + i * .012, when: t + .047 + i * .009, type: i % 3 === 0 ? 'sawtooth' : i % 2 ? 'square' : 'sine', gain: .045 + (i % 2) * .009, attack: .001, release: .072 + i * .009, priority: 89 - i, bus: 'sfx', tag: 'medium:debris', detune: d, pan: -.88 + (i / 7) * 1.76, filter: { type: 'bandpass', start: 7200 - i * 390, end: 640 + i * 45, q: 3.2 }, echo: .1, reverb: .3 }));
    // F5–F6: smoke tail and a delayed confirmation glint.
    this.note({ type: 'noise', duration: .31, when: t + .13, gain: .082, attack: .025, release: .245, priority: 76, bus: 'sfx', tag: 'medium:smoke', filter: { type: 'lowpass', start: 1150, end: 85, q: .75 }, pan: .14, reverb: .18 });
    if (combo > 0) this.bell({ midi: 77 + (combo % 5), when: t + .115, duration: .18, gain: .055, priority: 86, pan: .32, tag: 'medium:confirm', detune: d, reverb: .26 });
  }

  chainBurst(combo = 10): void {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + .003;
    const d = this.detune();
    this.duckMusic(.09, .36, .004, .065);
    // A large blast is not one louder sample: it is a timed field of five related detonations.
    this.transient({ when: t, gain: .235, priority: 100, tag: 'chainburst:initial', bright: 12000, duration: .031 });
    this.subImpact({ when: t + .006, midi: 40, glideTo: 18, duration: .58, gain: .37, priority: 100, tag: 'chainburst:sub' });
    const offsets = [0, .021, .049, .084, .126];
    const pans = [0, -.66, .64, -.28, .31];
    offsets.forEach((off, i) => {
      const pan = pans[i] ?? 0;
      this.note({ type: 'noise', duration: .34 + i * .045, when: t + off, gain: .205 - i * .012, attack: .001, release: .29 + i * .038, priority: 99 - i, bus: 'sfx', tag: 'chainburst:detonation', detune: d, pan, filter: { start: 8800 - i * 760, end: 82 + i * 48, q: 1.15 }, reverb: .33 });
      this.note({ midi: 43 + i * 2, glideTo: 20 + i, duration: .38 + i * .025, when: t + off, type: i % 2 ? 'triangle' : 'sine', gain: .185 - i * .008, attack: .001, release: .32 + i * .02, priority: 98 - i, bus: 'sfx', tag: 'chainburst:body', detune: d, pan });
    });
    // Wide shock ring is audible as opposite stereo sweeps.
    this.note({ midi: 57, glideTo: 91, duration: .23, when: t + .035, type: 'sine', gain: .075, attack: .01, release: .08, priority: 93, bus: 'sfx', tag: 'chainburst:ring-L', detune: d, pan: -.82, filter: { type: 'bandpass', start: 420, end: 7600, q: 6 }, echo: .16, reverb: .42 });
    this.note({ midi: 59, glideTo: 93, duration: .25, when: t + .043, type: 'sine', gain: .075, attack: .01, release: .085, priority: 93, bus: 'sfx', tag: 'chainburst:ring-R', detune: d, pan: .82, filter: { type: 'bandpass', start: 460, end: 7900, q: 6 }, echo: .16, reverb: .42 });
    const shards = this.rich(2) ? 16 : 10;
    for (let i = 0; i < shards; i += 1) {
      this.note({ midi: 80 + (i * 7) % 25, glideTo: 62 + (i * 3) % 12, duration: .085 + (i % 4) * .026, when: t + .075 + i * .008, type: i % 3 === 0 ? 'sawtooth' : i % 2 ? 'square' : 'sine', gain: .035 + (i % 3) * .006, attack: .001, release: .07 + (i % 4) * .02, priority: 88 - i % 8, bus: 'sfx', tag: 'chainburst:shard', detune: d, pan: -.94 + (i / (shards - 1)) * 1.88, filter: { type: 'bandpass', start: 7600 - (i % 6) * 620, end: 530 + (i % 5) * 95, q: 3 }, echo: .12, reverb: .36 });
    }
    this.note({ type: 'noise', duration: .58, when: t + .17, gain: .11, attack: .04, release: .48, priority: 78, bus: 'sfx', tag: 'chainburst:smoke-field', filter: { type: 'lowpass', start: 1450, end: 72, q: .8 }, reverb: .28 });
    this.chain(combo, .19);
  }

  overkillExplosion(): void {
    const c = this.ctx;
    if (!c) return;
    const pre = c.currentTime + .004;
    const flare = pre + .105;
    const cascade = flare + .075;
    const collapse = cascade + .24;
    const d = PHASE_CENTS.overdrive;
    this.duckMusic(.012, .92, .004, .19);
    // Armor exposure / charged hit: tension is created before loudness.
    this.note({ midi: 35, glideTo: 82, duration: .12, when: pre, type: 'sine', gain: .18, attack: .045, release: .028, priority: 100, bus: 'sfx', tag: 'overkill:charge', detune: d, filter: { type: 'bandpass', start: 180, end: 6200, q: 8 }, pan: -.12, reverb: .16 });
    this.note({ type: 'noise', duration: .11, when: pre, gain: .085, attack: .07, release: .028, priority: 98, bus: 'sfx', tag: 'overkill:suction', filter: { type: 'highpass', start: 800, end: 11800, q: .6 }, pan: .18 });
    this.transient({ when: flare, gain: .29, priority: 100, tag: 'overkill:flare-L', bright: 12500, duration: .038, pan: -.42 });
    this.transient({ when: flare + .008, gain: .235, priority: 100, tag: 'overkill:flare-R', bright: 10900, duration: .043, pan: .44 });
    this.subImpact({ when: flare, midi: 36, glideTo: 17, duration: .86, gain: .43, priority: 100, tag: 'overkill:core-sub' });
    // Six secondary explosions orbit the core instead of masking it at the same instant.
    const pans = [-.82, .72, -.45, .49, -.16, .18];
    pans.forEach((pan, i) => {
      const at = cascade + i * .046;
      this.note({ type: 'noise', duration: .42 + i * .055, when: at, gain: .225 - i * .01, attack: .001, release: .36 + i * .048, priority: 99 - i, bus: 'sfx', tag: 'overkill:secondary', detune: d, pan, filter: { start: 9000 - i * 680, end: 70 + i * 42, q: 1.1 }, reverb: .4 });
      this.note({ midi: 39 + i * 2, glideTo: 18 + i, duration: .48, when: at, type: i % 2 ? 'triangle' : 'sine', gain: .19, attack: .001, release: .41, priority: 98 - i, bus: 'sfx', tag: 'overkill:secondary-body', detune: d, pan });
    });
    // Implosion makes the following reward cadence feel larger without pushing the limiter harder.
    this.note({ midi: 70, glideTo: 19, duration: .5, when: collapse, type: 'sine', gain: .265, attack: .004, release: .42, priority: 100, bus: 'sfx', tag: 'overkill:implosion', detune: d, filter: { type: 'bandpass', start: 2400, end: 145, q: 8 }, reverb: .34 });
    this.note({ type: 'noise', duration: .82, when: cascade + .06, gain: .13, attack: .06, release: .69, priority: 82, bus: 'sfx', tag: 'overkill:smoke-column', filter: { type: 'lowpass', start: 1700, end: 58, q: .72 }, reverb: .38 });
    [60, 67, 72, 76, 79, 84].forEach((m, i) => this.note({ midi: m, duration: .62 + i * .055, when: collapse + .19 + i * .035, type: i < 2 ? 'triangle' : i % 2 ? 'sine' : 'pulse25', gain: .082 / (1 + i * .08), attack: .004, release: .5 + i * .04, priority: 96 - i, bus: 'sfx', tag: 'overkill:reward-crown', detune: d, pan: -.72 + i * .29, echo: .19, reverb: .68 }));
    if (this.rich(2)) {
      [96, 103, 108, 115].forEach((m, i) => this.bell({ midi: m, when: collapse + .37 + i * .055, duration: .34 + i * .045, gain: .036, priority: 84 - i, pan: -.7 + i * .47, tag: 'overkill:afterglow', detune: d, reverb: .74 }));
    }
  }

  chain(combo = 1, delay = 0): void {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + .004 + Math.max(0, delay);
    const phase = this.phase;
    const motif = [0, 2, 4, 7, 9, 12, 14, 16, 19, 21, 23, 24];
    const idx = Math.max(0, combo - 1) % motif.length;
    const cycle = Math.min(1, Math.floor(Math.max(0, combo - 1) / motif.length));
    let base = Math.min(101, 71 + (motif[idx] ?? 0) + cycle * 12);
    let voices = [0];
    let types: NoteType[] = ['pulse25'];
    if (phase === 'rush') {
      voices = [0, 7];
      types = ['pulse25', 'sine'];
    }
    if (phase === 'fever') {
      voices = combo >= 15 ? [0, 4, 7, 12] : combo >= 12 ? [0, 7, 12] : [0, 12];
      types = ['pulse25', 'sine', 'sine', 'square'];
    }
    if (phase === 'overdrive') {
      base -= 8;
      voices = [-12, 0, 7, 12];
      types = ['triangle', 'pulse25', 'sine', 'square'];
    }
    voices.forEach((iv, i) => this.note({ midi: base + iv, duration: phase === 'rush' ? .11 : .145 + i * .012, when: t + i * .0035, type: types[i] ?? 'sine', gain: (phase === 'overdrive' ? .125 : .112) / (1 + i * .19), attack: .0013, release: phase === 'rush' ? .078 : .105, priority: 84 - i, bus: 'sfx', tag: `chain:${combo}`, detune: this.detune(), pan: (i - (voices.length - 1) / 2) * .3, echo: .13, reverb: .18 + this.profile().density * .035 }));
    if (phase === 'overdrive') this.subImpact({ when: t, midi: 36, glideTo: 27, duration: .115, gain: .13, priority: 80, tag: 'chain:overdrive-sub' });
    if ([4, 8, 9, 12, 15, 19, 22, 24].includes(combo)) {
      const cadence = phase === 'overdrive' ? [0, 7, 12] : combo >= 15 ? [0, 4, 7, 12] : [0, 4, 7];
      cadence.forEach((iv, i) => this.note({ midi: Math.min(112, base - 12 + iv), duration: .18 + i * .015, when: t + .058 + i * .018, type: i === 0 ? 'pulse25' : 'sine', gain: .068 / (1 + i * .13), attack: .0015, release: .13, priority: 86 - i, bus: 'sfx', tag: `chain:cadence-${combo}`, detune: this.detune(), pan: -.38 + i * .25, echo: .18, reverb: .3 }));
    }
  }

  scorePop(points = 1000, delay = 0): void {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + .008 + Math.max(0, delay);
    const d = this.detune();
    const tier = points >= 10000 ? 3 : points >= 5000 ? 2 : points >= 3000 ? 1.5 : 1;
    const root = points >= 10000 ? 84 : points >= 5000 ? 81 : points >= 3000 ? 78 : 75;
    this.duckMusic(points >= 10000 ? .24 : .42, points >= 10000 ? .3 : .18, .004, .024, delay);
    this.transient({ when: t, gain: .07 + .018 * tier, priority: 91, tag: 'score:coin-snap', bright: 11200, duration: .018 });
    [0, 4, 7, 12].forEach((iv, i) => this.bell({ midi: root + iv, when: t + i * .038, duration: .24 + i * .045, gain: (.098 + .012 * tier) / (1 + i * .08), priority: 88 + i, pan: -.42 + i * .28, tag: `score:${points}`, detune: d, reverb: .34 + .04 * tier }));
    if (this.rich(1)) {
      [19, 24].forEach((iv, i) => this.bell({ midi: root + iv, when: t + .145 + i * .045, duration: .2 + i * .04, gain: .05 + .006 * tier, priority: 84 - i, pan: i ? -.58 : .58, tag: `score:shimmer-${points}`, detune: d, reverb: .46 }));
    }
    if (points >= 10000) this.subImpact({ when: t + .018, midi: 41, glideTo: 27, duration: .3, gain: .19, priority: 92, tag: 'score:10000-sub' });
  }

  speedLine(when?: number): void {
    const c = this.ctx;
    if (!c) return;
    const t = when ?? c.currentTime + .003;
    const d = this.detune();
    [-.72, .72].forEach((pan, i) => this.note({ type: 'noise', duration: .25 + i * .025, when: t + i * .016, gain: .075, attack: .045, release: .17, priority: 82 - i, bus: 'sfx', tag: 'rush:speed-air', detune: d, filter: { type: 'bandpass', start: 620 + i * 180, end: 8200 - i * 600, q: 2.2 }, pan, echo: .08, reverb: .12 }));
    this.note({ midi: 54, glideTo: 96, duration: .2, when: t + .008, type: 'sine', gain: .085, attack: .006, release: .14, priority: 84, bus: 'sfx', tag: 'rush:speed-tone', detune: d, pan: .32, reverb: .14 });
    if (this.rich(2)) this.note({ midi: 59, glideTo: 103, duration: .18, when: t + .022, type: 'pulse25', gain: .038, attack: .004, release: .13, priority: 78, bus: 'sfx', tag: 'rush:speed-shine', detune: d, pan: -.36, echo: .16, reverb: .16 });
  }

  shockRing(variant: ShockRingVariant = 'fever', when?: number): void {
    const c = this.ctx;
    if (!c) return;
    const t = when ?? c.currentTime + .004;
    const heavy = variant === 'overdrive';
    this.note({ midi: heavy ? 38 : 45, glideTo: heavy ? 75 : 79, duration: heavy ? .52 : .41, when: t, type: 'sine', gain: heavy ? .245 : .185, attack: .003, release: heavy ? .42 : .33, priority: 94, bus: 'sfx', tag: `${variant}:ring-rise`, filter: { type: 'bandpass', start: heavy ? 340 : 620, end: heavy ? 3900 : 4800, q: heavy ? 6.5 : 5.3 }, echo: .16, reverb: .5 });
    this.note({ midi: heavy ? 91 : 96, glideTo: heavy ? 55 : 67, duration: heavy ? .46 : .34, when: t + .012, type: 'sine', gain: heavy ? .092 : .075, attack: .002, release: heavy ? .37 : .27, priority: 90, bus: 'sfx', tag: `${variant}:ring-fall`, pan: .18, echo: .2, reverb: .55 });
    this.note({ type: 'noise', duration: heavy ? .34 : .24, when: t + .025, gain: heavy ? .15 : .115, attack: .012, release: heavy ? .27 : .19, priority: 89, bus: 'sfx', tag: `${variant}:air`, filter: { type: 'highpass', start: 2500, end: 9400, q: .75 }, pan: -.18, reverb: .34 });
  }

  rushStart(): void {
    const c = this.ctx;
    if (!c) return;
    const pre = c.currentTime + .008;
    const hit = pre + .065;
    const d = PHASE_CENTS.rush;
    this.duckMusic(.2, .235, .005, .052);
    this.note({ type: 'noise', duration: .085, when: pre, gain: .075, attack: .055, release: .027, priority: 86, bus: 'sfx', tag: 'rush:suction', filter: { type: 'highpass', start: 1200, end: 9800, q: .7 }, pan: -.2, reverb: .11 });
    this.transient({ when: hit, gain: .17, priority: 99, tag: 'rush:impact', bright: 10800, duration: .025 });
    this.subImpact({ when: hit, midi: 48, glideTo: 33, duration: .18, gain: .205, priority: 96, tag: 'rush:body' });
    [72, 76, 79, 84].forEach((m, i) => this.note({ midi: m, duration: .14 + i * .025, when: hit + i * .045, type: i === 0 ? 'pulse25' : i === 3 ? 'sine' : 'square', gain: .125 / (1 + i * .08), attack: .0015, release: .1 + i * .012, priority: 96 - i, bus: 'sfx', tag: 'rush:start', detune: d, pan: -.42 + i * .28, echo: .14, reverb: .3 }));
    this.speedLine(hit + .008);
    if (this.rich(1)) {
      [88, 91].forEach((m, i) => this.note({ midi: m, duration: .21, when: hit + .18 + i * .035, type: 'sine', gain: .056, attack: .001, release: .16, priority: 87 - i, bus: 'sfx', tag: 'rush:answer', detune: d, pan: i ? -.5 : .5, reverb: .42 }));
    }
  }

  feverStart(): void {
    const c = this.ctx;
    if (!c) return;
    const pre = c.currentTime + .008;
    const hit = pre + .098;
    this.duckMusic(.055, .48, .006, .075);
    this.note({ type: 'noise', duration: .125, when: pre, gain: .09, attack: .082, release: .038, priority: 89, bus: 'sfx', tag: 'fever:suction', filter: { type: 'highpass', start: 900, end: 11200, q: .65 }, pan: -.3, reverb: .13 });
    this.note({ midi: 48, glideTo: 86, duration: .125, when: pre, type: 'sine', gain: .075, attack: .055, release: .04, priority: 90, bus: 'sfx', tag: 'fever:riser', filter: { type: 'bandpass', start: 350, end: 5600, q: 5 }, pan: .28 });
    this.transient({ when: hit, gain: .205, priority: 100, tag: 'fever:impact-L', bright: 11600, duration: .028, pan: -.35 });
    this.transient({ when: hit + .006, gain: .16, priority: 99, tag: 'fever:impact-R', bright: 9800, duration: .032, pan: .38 });
    this.subImpact({ when: hit, midi: 42, glideTo: 24, duration: .31, gain: .27, priority: 100, tag: 'fever:sub' });
    [72, 76, 79, 84, 88].forEach((m, i) => this.note({ midi: m, duration: .2 + i * .032, when: hit + i * .052, type: i < 2 ? 'pulse25' : i === 4 ? 'sine' : 'square', gain: .135 / (1 + i * .08), attack: .0015, release: .145 + i * .012, priority: 98 - i, bus: 'sfx', tag: 'fever:fanfare', pan: -.5 + i * .25, echo: .16, reverb: .38 }));
    [60, 64, 67, 72].forEach((m, i) => this.note({ midi: m, duration: .68, when: hit + .145, type: i % 2 ? 'sine' : 'triangle', gain: .075 / (1 + i * .08), attack: .012, release: .54, priority: 93 - i, bus: 'sfx', tag: 'fever:chord', pan: -.48 + i * .32, echo: .14, reverb: .5 }));
    this.shockRing('fever', hit + .035);
    if (this.rich(2)) {
      [91, 96, 100, 103].forEach((m, i) => this.note({ midi: m, duration: .16 + i * .025, when: hit + .26 + i * .03, type: i % 2 ? 'sine' : 'square', gain: .04, attack: .001, release: .12, priority: 84 - i, bus: 'sfx', tag: 'fever:stardust', pan: -.66 + i * .44, echo: .18, reverb: .55 }));
    }
  }

  overdriveStart(): void {
    const c = this.ctx;
    if (!c) return;
    const pre = c.currentTime + .006;
    const hit = pre + .19;
    const d = PHASE_CENTS.overdrive;
    this.duckMusic(.025, .62, .006, .14);
    this.transient({ when: pre, gain: .09, priority: 96, tag: 'overdrive:latch', bright: 11800, duration: .018 });
    this.note({ midi: 31, glideTo: 77, duration: .225, when: pre, type: 'sine', gain: .245, attack: .008, release: .05, priority: 99, bus: 'sfx', tag: 'overdrive:charge', detune: d, filter: { type: 'bandpass', start: 220, end: 5200, q: 7 }, echo: .08, reverb: .22 });
    this.note({ midi: 43, glideTo: 86, duration: .205, when: pre + .012, type: 'sawtooth', gain: .072, attack: .025, release: .05, priority: 94, bus: 'sfx', tag: 'overdrive:charge-edge', detune: d, filter: { type: 'bandpass', start: 500, end: 7200, q: 4.2 }, pan: .24 });
    this.note({ type: 'noise', duration: .205, when: pre, gain: .1, attack: .145, release: .05, priority: 92, bus: 'sfx', tag: 'overdrive:suction', filter: { type: 'highpass', start: 800, end: 11800, q: .6 }, pan: -.28 });
    this.transient({ when: hit, gain: .235, priority: 100, tag: 'overdrive:ignition-L', bright: 12000, duration: .03, pan: -.38 });
    this.transient({ when: hit + .007, gain: .19, priority: 100, tag: 'overdrive:ignition-R', bright: 10400, duration: .034, pan: .4 });
    this.subImpact({ when: hit, midi: 35, glideTo: 19, duration: .56, gain: .34, priority: 100, tag: 'overdrive:sub' });
    this.note({ type: 'noise', duration: .5, when: hit + .008, gain: .22, attack: .001, release: .43, priority: 99, bus: 'sfx', tag: 'overdrive:burst', detune: d, filter: { start: 8400, end: 95, q: 1.15 }, reverb: .3 });
    [36, 43, 48, 55, 60, 67, 72].forEach((m, i) => this.note({ midi: m, duration: .82, when: hit + .02, type: i < 2 ? 'triangle' : i % 2 ? 'sine' : 'pulse25', gain: .09 / (1 + i * .075), attack: .008, release: .65, priority: 97 - i, bus: 'sfx', tag: 'overdrive:chord', detune: d, pan: -.72 + i * .24, echo: .13, reverb: .52 }));
    this.shockRing('overdrive', hit + .03);
    if (this.rich(2)) {
      [84, 91, 96, 103].forEach((m, i) => this.bell({ midi: m, when: hit + .28 + i * .035, duration: .28 + i * .035, gain: .045, priority: 86 - i, pan: -.62 + i * .41, tag: 'overdrive:crown', detune: d, reverb: .58 }));
    }
  }

  massHit(combo = 1): void {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + .003;
    const d = this.detune();
    const count = this.rich(2) ? 5 : this.rich(1) ? 4 : 3;
    this.duckMusic(this.phase === 'fever' ? .16 : .24, this.phase === 'fever' ? .16 : .12, .003, .025);
    this.transient({ when: t, gain: .19, priority: 98, tag: 'mass:initial', bright: 11000, duration: .028 });
    this.subImpact({ when: t + .004, midi: this.phase === 'overdrive' ? 39 : 44, glideTo: 23, duration: .34, gain: .285, priority: 99, tag: 'mass:sub' });
    const offsets = [0, .018, .043, .071, .108];
    for (let i = 0; i < count; i += 1) {
      const pan = -.78 + i * (1.56 / Math.max(1, count - 1));
      const at = t + (offsets[i] ?? 0);
      this.note({ type: 'noise', duration: .25 + i * .025, when: at, gain: .16, attack: .001, release: .21, priority: 94 - i, bus: 'sfx', tag: 'mass:burst', detune: d, pan, filter: { start: 6500 - i * 650, end: 120 + i * 55, q: 1.15 }, reverb: .24 });
      this.note({ midi: 48 + i * 2, glideTo: 28 + i, duration: .23 + i * .018, when: at, type: i % 2 ? 'triangle' : 'sine', gain: .155, attack: .001, release: .19, priority: 95 - i, bus: 'sfx', tag: 'mass:body', detune: d, pan });
    }
    const sparks = this.rich(2) ? 6 : 4;
    for (let i = 0; i < sparks; i += 1) {
      this.note({ midi: 90 + i * 3 + (i % 2 ? 4 : 0), duration: .075 + i * .008, when: t + .052 + i * .015, type: i % 2 ? 'square' : 'sine', gain: .052, attack: .001, release: .058, priority: 88 - i, bus: 'sfx', tag: 'mass:shard', detune: d, pan: -.72 + i * (1.44 / Math.max(1, sparks - 1)), echo: .12, reverb: .31 });
    }
    if (combo >= 15 && this.rich(1)) this.shockRing(this.phase === 'overdrive' ? 'overdrive' : 'fever', t + .045);
  }

  eliteBreak(): void {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + .003;
    const d = this.detune();
    this.duckMusic(.07, .34, .004, .055);
    this.transient({ when: t, gain: .22, priority: 100, tag: 'elite:armor-snap', bright: 11200, duration: .032 });
    this.note({ type: 'noise', duration: .5, when: t + .004, gain: .25, attack: .001, release: .43, priority: 99, bus: 'sfx', tag: 'elite:crack', detune: d, filter: { start: 7200, end: 105, q: 1.25 }, reverb: .24 });
    this.subImpact({ when: t + .006, midi: 43, glideTo: 22, duration: .5, gain: .345, priority: 100, tag: 'elite:sub' });
    this.note({ midi: 84, glideTo: 55, duration: .38, when: t + .018, type: 'sawtooth', gain: .105, attack: .001, release: .32, priority: 98, bus: 'sfx', tag: 'elite:metal', detune: d, filter: { type: 'bandpass', start: 6200, end: 610, q: 4.6 }, pan: .22, echo: .2, reverb: .35 });
    [-.65, .1, .65].forEach((pan, i) => this.note({ midi: 92 + i * 4, duration: .16 + i * .035, when: t + .1 + i * .032, type: i % 2 ? 'square' : 'sine', gain: .05, attack: .001, release: .12, priority: 91 - i, bus: 'sfx', tag: 'elite:ricochet', detune: d, pan, echo: .18, reverb: .42 }));
    if (this.rich(1)) this.note({ type: 'noise', duration: .32, when: t + .18, gain: .095, attack: .002, release: .27, priority: 87, bus: 'sfx', tag: 'elite:collapse', filter: { type: 'lowpass', start: 1100, end: 90, q: .9 }, pan: -.24, reverb: .2 });
  }

  bossPartBreak(): void {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + .003;
    const d = PHASE_CENTS.overdrive;
    this.duckMusic(.045, .38, .004, .07);
    this.transient({ when: t, gain: .235, priority: 100, tag: 'boss:part-snap', bright: 11800, duration: .033 });
    this.subImpact({ when: t + .004, midi: 40, glideTo: 20, duration: .52, gain: .36, priority: 100, tag: 'boss:part-sub' });
    [-.72, -.24, .28, .7].forEach((pan, i) => {
      const at = t + i * .027;
      this.note({ type: 'noise', duration: .37 + i * .045, when: at, gain: .21, attack: .001, release: .32, priority: 99 - i, bus: 'sfx', tag: 'boss:part-noise', detune: d, pan, filter: { start: 7600 - i * 720, end: 90 + i * 58, q: 1.25 }, reverb: .3 });
      this.note({ midi: 43 + i * 3, glideTo: 23 + i, duration: .42, when: at, type: i % 2 ? 'triangle' : 'sine', gain: .19, attack: .001, release: .35, priority: 99 - i, bus: 'sfx', tag: 'boss:part-body', detune: d, pan });
    });
    [84, 91, 96, 100].forEach((m, i) => this.note({ midi: m, glideTo: m - 17, duration: .28, when: t + .09 + i * .028, type: i % 2 ? 'square' : 'sawtooth', gain: .055, attack: .001, release: .23, priority: 92 - i, bus: 'sfx', tag: 'boss:part-shard', detune: d, pan: -.6 + i * .4, echo: .18, reverb: .4 }));
  }

  bossCoreBreak(): void {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + .003;
    const d = PHASE_CENTS.overdrive;
    const blast = t + .052;
    const collapse = t + .19;
    this.duckMusic(.018, .74, .004, .17);
    this.transient({ when: t, gain: .26, priority: 100, tag: 'boss:core-fracture-L', bright: 12000, duration: .034, pan: -.4 });
    this.transient({ when: t + .009, gain: .22, priority: 100, tag: 'boss:core-fracture-R', bright: 10500, duration: .04, pan: .42 });
    this.note({ midi: 74, glideTo: 42, duration: .18, when: t, type: 'sawtooth', gain: .11, attack: .001, release: .14, priority: 99, bus: 'sfx', tag: 'boss:core-crack-tone', detune: d, filter: { type: 'bandpass', start: 6500, end: 380, q: 5 }, reverb: .22 });
    this.subImpact({ when: blast, midi: 37, glideTo: 18, duration: .7, gain: .4, priority: 100, tag: 'boss:core-sub' });
    [-.78, -.26, .26, .78].forEach((pan, i) => {
      const at = blast + i * .031;
      this.note({ type: 'noise', duration: .55 + i * .045, when: at, gain: .23, attack: .001, release: .48, priority: 100 - i, bus: 'sfx', tag: 'boss:core-burst', detune: d, pan, filter: { start: 8800 - i * 820, end: 75 + i * 46, q: 1.15 }, reverb: .36 });
      this.note({ midi: 40 + i * 3, glideTo: 20 + i, duration: .58, when: at, type: i % 2 ? 'triangle' : 'sine', gain: .205, attack: .001, release: .49, priority: 99 - i, bus: 'sfx', tag: 'boss:core-body', detune: d, pan });
    });
    this.note({ midi: 64, glideTo: 20, duration: .52, when: collapse, type: 'sine', gain: .24, attack: .004, release: .43, priority: 99, bus: 'sfx', tag: 'boss:core-implosion', detune: d, filter: { type: 'bandpass', start: 1900, end: 180, q: 7 }, reverb: .32 });
    [72, 79, 84, 91].forEach((m, i) => this.note({ midi: m, duration: .42 + i * .045, when: t + .31 + i * .042, type: i === 0 ? 'pulse25' : 'sine', gain: .068 / (1 + i * .1), attack: .0015, release: .34 + i * .03, priority: 94 - i, bus: 'sfx', tag: 'boss:core-resolve', detune: d, pan: -.62 + i * .42, echo: .16, reverb: .62 }));
    this.shockRing('overdrive', blast + .025);
    if (this.rich(2)) {
      [103, 110].forEach((m, i) => this.note({ midi: m, duration: .32 + i * .06, when: t + .45 + i * .055, type: 'sine', gain: .034, attack: .001, release: .27, priority: 82 - i, bus: 'sfx', tag: 'boss:core-stardust', pan: i ? -.58 : .58, echo: .2, reverb: .7 }));
    }
  }

  comboBreak(): void {
    const c = this.ctx;
    if (!c) return;
    const t = c.currentTime + .004;
    this.duckMusic(.38, .28, .004, .025);
    this.transient({ when: t, gain: .075, priority: 88, tag: 'combo:break-snap', bright: 6800, duration: .025 });
    [69, 65, 60, 53].forEach((m, i) => this.note({ midi: m, duration: .105 + i * .01, when: t + i * .058, type: 'sawtooth', gain: .09, attack: .001, release: .083, priority: 85 - i, bus: 'sfx', tag: 'combo:break', filter: { start: 2600, end: 330, q: 1.5 }, pan: i % 2 ? -.2 : .2, reverb: .12 }));
    this.subImpact({ when: t + .13, midi: 42, glideTo: 29, duration: .23, gain: .14, priority: 82, tag: 'combo:break-body' });
  }

  panic(): void {
    this.allocator.panic();
  }

  dispose(): void {
    this.allocator.panic();
    window.clearTimeout(this.reverbDisconnectTimer);
    window.clearTimeout(this.delayDisconnectTimer);
    this.reverbConnected = false;
    this.delayConnected = false;
    this.technoEventBuffers = [];
    this.technoComboBuffers = [];
    this.technoFormationBuffers = [];
    this.technoSampleLoad = null;
    const nodes = this.nodes;
    this.nodes = null;
    // Fix 1: a shared context belongs to its owner (AudioEngine) — closing it here
    // would kill that engine's audio too, and closing twice rejects.
    if (nodes?.ownsContext) void nodes.ctx.close().catch(() => undefined);
  }
}

/**
 * Phase-aware arcade BGM. Sixteen steps a bar, four bars, scheduled ahead on a
 * 25ms look-ahead timer so the pattern never rides the render clock; the phase
 * decides tempo lift, transposition, drum density and which extra layers play.
 */
export class StepSequencer {
  baseBpm = 138;
  running = false;
  private step = 0;
  private nextStepTime = 0;
  private timer = 0;
  private readonly lookAheadMs = 25;
  private readonly scheduleAhead = .12;
  private readonly root = 45;
  private readonly progression = [0, 5, 8, 3];

  constructor(private readonly engine: CombatAudioEngine, private readonly getPhaseInfo: () => PhaseInfo) {}

  setTempo(bpm: number): void {
    this.baseBpm = bpm;
  }

  effectiveBpm(): number {
    const p = this.getPhaseInfo();
    const lift: Record<CombatPhase, number> = { normal: 1, rush: 1.035, fever: 1.055, overdrive: 1.07 };
    return this.baseBpm * (lift[p.phase] || 1);
  }

  stepDuration(): number {
    return 60 / this.effectiveBpm() / 4;
  }

  /** The ctx check comes first: a press before the unlock resolves must not latch `running`. */
  start(): void {
    const c = this.engine.ctx;
    if (!c || this.running) return;
    this.running = true;
    this.step = 0;
    this.nextStepTime = c.currentTime + .06;
    this.timer = window.setInterval(() => this.scheduler(), this.lookAheadMs);
    this.scheduler();
  }

  stop(): void {
    this.running = false;
    window.clearInterval(this.timer);
    this.timer = 0;
  }

  scheduler(): void {
    const c = this.engine.ctx;
    if (!this.running || !c) return;
    while (this.nextStepTime < c.currentTime + this.scheduleAhead) {
      this.scheduleStep(this.step, this.nextStepTime);
      this.nextStepTime += this.stepDuration();
      this.step = (this.step + 1) % 64;
    }
  }

  scheduleStep(step: number, when: number): void {
    const e = this.engine;
    const p = this.getPhaseInfo();
    const phase = p.phase;
    const local = step % 16;
    const bar = Math.floor(step / 16);
    const density = e.profile().density;
    const transpose = phase === 'fever' ? Math.round(p.intensity * 3) : phase === 'rush' ? 1 : phase === 'overdrive' ? 2 : 0;
    const root = this.root + (this.progression[bar] ?? 0) + transpose;
    const sixteenth = this.stepDuration();
    const kickSteps = phase === 'overdrive' ? [0, 6, 8, 14] : phase === 'fever' ? [0, 7, 8, 14] : [0, 8];
    if (kickSteps.includes(local)) e.note({ midi: phase === 'overdrive' ? 32 : 35, duration: .13, when, type: 'sine', gain: phase === 'overdrive' ? .205 : .18, attack: .001, release: .105, priority: 18, bus: 'music', tag: 'drum:kick', glideTo: phase === 'overdrive' ? 20 : 24 });
    if ([4, 12].includes(local)) {
      e.note({ type: 'noise', duration: .105, when, gain: phase === 'overdrive' ? .105 : .09, attack: .001, release: .08, priority: 16, bus: 'music', tag: 'drum:snare', filter: { type: 'bandpass', start: 2600, end: 760, q: .95 } });
      e.note({ midi: 55, duration: .075, when, type: 'triangle', gain: .074, attack: .001, release: .054, priority: 16, bus: 'music', tag: 'drum:snare-body' });
    }
    const hatEvery = phase === 'normal' ? 2 : 1;
    if (local % hatEvery === 0) e.note({ type: 'noise', duration: .024, when, gain: phase === 'normal' ? .026 : phase === 'overdrive' ? .042 : .037, attack: .001, release: .017, priority: 4, bus: 'music', tag: 'drum:hat', pan: local % 4 ? .34 : -.34, filter: { type: 'highpass', start: 7200, end: 5600, q: .4 } });
    if (phase !== 'normal' && density > 0 && [7, 15].includes(local)) e.note({ type: 'noise', duration: sixteenth * .86, when, gain: .028, attack: .002, release: sixteenth * .72, priority: 3, bus: 'music', tag: 'drum:open-hat', pan: local === 7 ? -.48 : .48, filter: { type: 'highpass', start: 6200, end: 3900, q: .55 } });
    const bass = [0, null, 0, 7, 0, null, 10, 7, 0, null, 0, 12, 10, 7, 3, null][local];
    if (bass !== null && bass !== undefined) {
      e.note({ midi: root + bass - (phase === 'overdrive' ? 12 : 0), duration: sixteenth * .88, when, type: 'triangle', gain: phase === 'overdrive' ? .135 : .112, attack: .003, release: .052, priority: 12, bus: 'music', tag: 'bass', filter: { start: phase === 'overdrive' ? 1150 : 1500, end: phase === 'overdrive' ? 280 : 430, q: 1.15 } });
      if (phase === 'overdrive') e.note({ midi: root + bass, duration: sixteenth * .7, when, type: 'sine', gain: .048, attack: .002, release: .04, priority: 7, bus: 'music', tag: 'bass:octave' });
    }
    if (local === 0 || local === 8) {
      const chord = local === 0 ? [0, 3, 7, 10] : [0, 5, 7, 12];
      chord.forEach((iv, i) => e.note({ midi: root + 12 + iv, duration: sixteenth * 7.1, when, type: i % 2 ? 'sawtooth' : 'pulse25', gain: phase === 'normal' ? .031 : .041, attack: .034, release: .22, priority: 3, bus: 'music', pan: -.45 + i * .3, detune: i % 2 ? 4 : -4, tag: 'pad', filter: { start: phase === 'normal' ? 2100 : 3500, end: phase === 'overdrive' ? 850 : phase === 'normal' ? 680 : 1250, q: .7 } }));
    }
    const arp = [0, 7, 12, 15, 12, 7, 3, 7, 0, 10, 12, 19, 15, 12, 10, 7][local] ?? 0;
    e.note({ midi: root + 24 + arp, duration: sixteenth * .6, when, type: local % 4 === 0 ? 'pulse25' : 'sine', gain: phase === 'rush' ? .054 : .043, attack: .002, release: .038, priority: 6, bus: 'music', pan: Math.sin(step * .8) * .58, tag: 'arp' });
    const lead = [null, null, 12, null, 15, null, 19, 17, null, 15, 12, null, 10, null, 7, null][local];
    if (lead !== null && lead !== undefined) e.note({ midi: root + 24 + lead, duration: sixteenth * 1.5, when, type: 'pulse25', gain: .068, attack: .003, release: .085, priority: 9, bus: 'music', pan: .08, tag: 'lead', filter: { start: 4000, end: 1400, q: 1.7 } });
    if (phase === 'rush') {
      const run = [12, 15, 19, 22, 24, 22, 19, 15, 12, 17, 20, 24, 27, 24, 20, 17][local] ?? 0;
      if (local % 2 === 1) e.note({ midi: root + 30 + run, duration: sixteenth * .34, when, type: 'sine', gain: .034, attack: .001, release: .023, priority: 5, bus: 'music', pan: local % 4 === 1 ? -.68 : .68, tag: 'rush:line' });
      if ([3, 7, 11, 15].includes(local)) e.note({ type: 'noise', duration: sixteenth * .8, when, gain: .019, attack: .001, release: .05, priority: 2, bus: 'music', tag: 'rush:air', pan: local < 8 ? -.72 : .72, filter: { type: 'bandpass', start: 900, end: 6900, q: 1.8 } });
    }
    if (phase === 'fever') {
      const upper = [12, 19, 24, 27, 24, 19, 15, 19, 12, 22, 24, 31, 27, 24, 22, 19][local] ?? 0;
      e.note({ midi: root + 36 + upper, duration: sixteenth * .44, when, type: local % 2 ? 'sine' : 'square', gain: .027 + .014 * p.intensity, attack: .0015, release: .03, priority: 5, bus: 'music', pan: -Math.sin(step * .6) * .7, tag: 'fever:upper' });
      if ([0, 6, 8, 14].includes(local)) e.note({ midi: root + 24 + (local === 6 ? 10 : 7), duration: sixteenth * .78, when, type: 'sawtooth', gain: .048, attack: .004, release: .07, priority: 7, bus: 'music', tag: 'fever:stab', filter: { start: 4800, end: 1700, q: 1.8 } });
      if (density > 0 && [2, 10].includes(local)) e.note({ midi: root + 43, duration: sixteenth * .55, when, type: 'sine', gain: .026, attack: .002, release: .035, priority: 4, bus: 'music', tag: 'fever:glint', pan: local === 2 ? -.75 : .75 });
    }
    if (phase === 'overdrive') {
      if ([0, 8].includes(local)) e.note({ midi: root + 12, duration: sixteenth * 3.2, when, type: 'sine', gain: .074, attack: .01, release: .18, priority: 8, bus: 'music', tag: 'overdrive:pulse', filter: { type: 'bandpass', start: 330, end: 2500, q: 4.2 }, pan: local === 0 ? -.42 : .42 });
      if (lead !== null && lead !== undefined) e.note({ midi: root + 36 + lead, duration: sixteenth * 1.2, when, type: 'square', gain: .034, attack: .002, release: .07, priority: 5, bus: 'music', pan: -.52, tag: 'overdrive:wide-lead' });
      if (density > 0 && [3, 11].includes(local)) [0, 7, 12].forEach((iv, i) => e.note({ midi: root + 24 + iv, duration: sixteenth * .72, when: when + i * .004, type: i ? 'sine' : 'pulse25', gain: .032 / (1 + i * .16), attack: .002, release: .055, priority: 5 - i, bus: 'music', pan: -.5 + i * .5, tag: 'overdrive:power-stab' }));
    }
  }
}
